import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionService } from '@src/app/shared/services/session.service';

import { CustomerResponse, CustomersService, CustomerRequest, RetailersService } from '@src/app/shared/backend/clients';
import { firstValueFrom } from 'rxjs';
import { CountryService } from '@src/app/shared/services/countries-service';
import { latinCharsetRegexValidator } from '@src/app/shared/validators/latinCharacters.validator';
import { GenderService } from '@src/app/shared/services/customerGender.service';
import { emailRegexValidator } from '@src/app/shared/validators/email.validator';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';


@Component({
    selector: 'app-edit-customer',
    templateUrl: './edit-customer.component.html',
    styleUrl: './edit-customer.component.scss',
    host: {
        class: 'app-edit-customer'
    },
    standalone: false
})
export class EditCustomerComponent extends BaseViewComponent implements OnInit {

  customerId: number | undefined;
  customer: CustomerResponse = {};

  countries: any[] = [];
  selectedCountry: any;

  companyLinks = {
    brandPrivacy: '',
    brandTerms: ''
  };

  comboData: ComboData = new ComboData(async (filter) => await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter)));

  public customerDetailsFormGroup: FormGroup = this.fb.group({
    firstname: ['', [Validators.required, Validators.maxLength(50)]],
    lastname: ['', [Validators.required, Validators.maxLength(50)]],
    romanisedFirstName: ['', [latinCharsetRegexValidator(), Validators.maxLength(50)]],
    romanisedLastName: ['', [latinCharsetRegexValidator(), Validators.maxLength(50)]],
    email: ['', [emailRegexValidator(), Validators.maxLength(50)]],
    country: [[]],
    address: ['', [Validators.maxLength(50)]],
    postalCode: ['', [Validators.maxLength(50)]],
    birthDate: [null, [Validators.maxLength(10)]],
    gender: [[]],
    foreign: ['false'],
    wantEmails: [false],
    acceptedTerms: [true, [Validators.required]],
    acceptedPrivacyPolicy: [false, [Validators.required]]
  });

  genders: any = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private countryService: CountryService,
    private genderService: GenderService,
    private fb: FormBuilder,
    private customersService: CustomersService,
    private retailersService: RetailersService,
    private sessionService: SessionService
  ) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {

    const company = await this.sessionService.getCompanyData();

    this.companyLinks = {
      brandPrivacy: company.privacyPolicyLink ?? '',
      brandTerms: company.termsOfUseLink ?? ''
    };

    const wantEmails = this.customerDetailsFormGroup.get('wantRetailerEmails');
    const acceptedTerms = this.customerDetailsFormGroup.get('acceptedRetailerTerms');

    wantEmails?.disable();
    acceptedTerms?.disable();

    this.route.params.subscribe(async params => {
      this.customerId = Number(params['customerId']);
      if (this.customerId) {
        const [customer, countries] = await Promise.all([
          firstValueFrom(this.customersService.apiCustomersIdGet(this.customerId)),
          this.countryService.getCountries()
        ]);

        this.customer = customer;
        this.countries = countries;
        this.genders = this.genderService.getGenderOptions();

        this.initForm();
      }
    });
  }


  async onLazyLoadComboData(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.comboData.getData(search, activePage);
  }

  getComboTotalPages(): number {
    const totalRecords = this.comboData.totalRecords ?? 0;
    const pageSize = Number(this.comboData.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  initForm() {
    const selectedCountry = this.customer.isoCountry ? [this.customer.isoCountry.toUpperCase()] : [];
    const selectedGender = this.customer.gender ? [this.customer.gender] : [];
    const wantEmails = this.customer.wantEmails;
    const acceptedPrivacyPolicy = this.customer.acceptedPrivacyPolicy;
    const acceptedTerms = this.customer.acceptedTerms
    const isForeign = this.customer.foreign ? 'true' : 'false';
    const birthDate = this.customer.birthDate ? new Date(this.customer.birthDate) : null;

    this.customerDetailsFormGroup.patchValue({
      firstname: this.customer.firstName,
      lastname: this.customer.lastName,
      romanisedFirstName: this.customer?.romanisedFirstName,
      romanisedLastName: this.customer?.romanisedLastName,
      email: this.customer.email,
      address: this.customer.address,
      postalCode: this.customer.postalCode,
      birthDate: birthDate,
      gender: selectedGender,
      country: selectedCountry,
      foreign: isForeign,
      wantEmails: wantEmails,
      acceptedTerms: acceptedTerms,
      acceptedPrivacyPolicy: acceptedPrivacyPolicy
    });
  }

  async submitForm() {
    if (this.customerDetailsFormGroup.valid) {
      const parsedCustomerId = parseInt(this.customerId?.toString() || '0', 10);
      const birthDate = this.customerDetailsFormGroup.controls['birthDate'].value;
      const birthDateUtc = birthDate?.toISOString?.();
      const birthDateTimeless = birthDateUtc ? birthDateUtc.split('T')[0] : null;
      const customer: CustomerRequest = {
        firstName: this.customerDetailsFormGroup.controls['firstname'].value,
        lastName: this.customerDetailsFormGroup.controls['lastname'].value,
        romanisedFirstName: this.customerDetailsFormGroup.controls['romanisedFirstName'].value,
        romanisedLastName: this.customerDetailsFormGroup.controls['romanisedLastName'].value,
        email: this.customerDetailsFormGroup.controls['email'].value,
        isoCountry: this.customerDetailsFormGroup.controls['country'].value?.[0],
        address: this.customerDetailsFormGroup.controls['address'].value,
        postalCode: this.customerDetailsFormGroup.controls['postalCode'].value,
        birthDate: birthDateTimeless,
        gender: this.customerDetailsFormGroup.controls['gender'].value?.[0],
        foreign: this.customerDetailsFormGroup.controls['foreign'].value === 'true' ? true : false,
        wantEmails: this.customerDetailsFormGroup.controls['wantEmails'].value ,
        acceptedTerms: this.customerDetailsFormGroup.controls['acceptedTerms'].value,
        acceptedPrivacyPolicy: this.customerDetailsFormGroup.controls['acceptedPrivacyPolicy'].value
      };

      await firstValueFrom(this.customersService.apiCustomersUpdateIdPut(parsedCustomerId, customer));

      this.router.navigate(['/customers', this.customerId]);
    }
  }
}
